import { api, API } from "../api";

export function pushSupport() {
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent || "") || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || navigator.standalone === true;
  if (ios && !standalone) return "install-first";
  return window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window ? "supported" : "unsupported";
}

function applicationKey(value) {
  const raw = atob(value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export async function enableDevicePush(publicKey) {
  // Request permission directly from the click, before awaiting registration.
  const permission = Notification.permission === "granted" ? Promise.resolve("granted") : Notification.requestPermission();
  const registration = await navigator.serviceWorker.register("/push-worker.js", { scope: "/", updateViaCache: "none" });
  if (await permission !== "granted") throw new Error("Notifications were not allowed. You can enable them in your phone's notification settings.");
  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationKey(publicKey) });
  try {
    await api.post("/notifications/push/subscribe", subscription.toJSON());
  } catch (error) {
    await subscription.unsubscribe();
    throw error;
  }
  return subscription;
}

export async function disableDevicePush(token) {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration("/push-worker.js");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  try {
    if (token) {
      // The shared interceptor reloads the current token. Logout must retain the
      // previous identity even if another account signs in before this awaits.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
      const response = await fetch(`${API}/notifications/push/unsubscribe`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Could not revoke the previous device notification consent");
      } finally { clearTimeout(timer); }
    } else {
      await api.post("/notifications/push/unsubscribe", { endpoint: subscription.endpoint });
    }
  } finally {
    // Even offline logout stops this browser receiving the previous account's pushes.
    await subscription.unsubscribe();
    if (navigator.clearAppBadge) await navigator.clearAppBadge().catch(() => {});
  }
}
